import { Component, computed, Signal, inject, effect, ChangeDetectionStrategy } from '@angular/core'
import { ActivatedRoute, Router, RouterLink } from '@angular/router'
import { toSignal } from '@angular/core/rxjs-interop'
import { map } from 'rxjs/operators'
import { StateService } from '../../services/state.service'
import { addIcons } from 'ionicons'
import { arrowBackOutline, arrowForwardOutline } from 'ionicons/icons'
import {
  IonButtons,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonMenuButton,
  IonContent,
  IonButton,
  IonIcon,
  IonGrid,
  IonRow,
  IonCol,
  IonRouterLinkWithHref,
} from '@ionic/angular'

import { TranslatePipe } from '@ngx-translate/core'
import { FirebaseAnalyticsService } from '../../services/firebase-analytics.service'
import { InjectHTMLDirective } from '../../directives/inject-html.directive'

interface ArticleNavigation {
  slug: string
  title: string
}

@Component({
  selector: 'app-article',
  templateUrl: './article-page.component.html',
  styleUrl: './article-page.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    IonButtons,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonMenuButton,
    IonContent,
    IonButton,
    IonIcon,
    RouterLink,
    IonGrid,
    IonRow,
    IonCol,
    TranslatePipe,
    IonRouterLinkWithHref,
    InjectHTMLDirective,
  ],
})
export class ArticlePageComponent {
  private route = inject(ActivatedRoute)
  private stateService = inject(StateService)
  private router = inject(Router)
  private analyticsService = inject(FirebaseAnalyticsService)

  private readonly articleSlug = toSignal(this.route.paramMap.pipe(map((params) => params.get('slug') ?? '')))

  private readonly article = computed(() => this.stateService.getArticle(this.articleSlug() ?? ''))

  readonly title = computed(() => this.article()?.menutitle)
  readonly content = computed(() => this.article()?.content ?? '')
  readonly icon = computed(() => this.article()?.icon)

  readonly treatmentPlace: Signal<string | undefined>
  readonly medicalTrail: Signal<string | undefined>

  private allArticles = this.stateService.allArticlesSorted
  private readonly currentIndex = computed(() => {
    const article = this.article()
    if (!article) return -1
    return this.allArticles().findIndex((a) => a.slug === article.slug)
  })

  readonly prevArticle = computed<ArticleNavigation | undefined>(() => {
    const idx = this.currentIndex()
    if (idx <= 0) return undefined
    const prev = this.allArticles()[idx - 1]
    return prev ? { slug: prev.slug, title: prev.menutitle } : undefined
  })

  readonly nextArticle = computed<ArticleNavigation | undefined>(() => {
    const idx = this.currentIndex()
    const articles = this.allArticles()
    if (idx < 0 || idx >= articles.length - 1) return undefined
    const next = articles[idx + 1]
    return next ? { slug: next.slug, title: next.menutitle } : undefined
  })

  constructor() {
    addIcons({ arrowBackOutline, arrowForwardOutline })
    this.medicalTrail = computed(() => {
      const medicalTrail = this.stateService.medicalTrail()
      if (medicalTrail?.includes('enrolled-with-video')) {
        return 'enrolled-with-video'
      } else if (medicalTrail?.includes('enrolled-without-video')) {
        return 'enrolled-without-video'
      } else {
        return medicalTrail
      }
    })
    this.treatmentPlace = this.stateService.currentTreatmentPlace

    effect(() => {
      if (!this.article()) {
        this.router.navigate([''])
      }
    })
  }

  async navLinkClick(direction: 'prev' | 'next') {
    const article = direction === 'next' ? this.nextArticle() : this.prevArticle()
    if (article) {
      await this.analyticsService.trackEvent(`article_${direction}`, article)
    }
  }
}
